/**
 * InteractionDisabler — injects editor CSS styles into the document.
 * ~518 lines of CSS are kept as a template string (intentionally).
 */

import { CONFIG } from './config'
import { log } from './utils'

export class InteractionDisabler {
  private styleElement: HTMLStyleElement | null = null

  private _isEnabled = false

  get isEnabled(): boolean {
    return this._isEnabled
  }

  enable(): void {
    if (this._isEnabled) {
      log('交互禁用已启用，跳过')
      return
    }
    this.injectStyles()
    this._isEnabled = true
    log('交互禁用样式已注入')
  }

  disable(): void {
    if (!this._isEnabled) return
    if (this.styleElement) {
      this.styleElement.remove()
      this.styleElement = null
    }
    this._isEnabled = false
    log('交互禁用样式已移除')
  }

  private injectStyles(): void {
    const style = document.createElement('style')
    style.setAttribute(CONFIG.EDITOR_STYLE_ATTR, 'true')
    style.textContent = `
        /* Musk Editor 样式 */

        /* 图标尺寸由 ICONS 的内联重要样式保护，勿用 auto 覆盖其设计宽高。
         * 此处不统一重置浮层 SVG，避免影响 AI 预览内容或公式自身的尺寸。
         */

        [${CONFIG.EDITABLE_ATTR}="true"] {
          cursor: text !important;
        }

        [${CONFIG.EDITABLE_ATTR}="true"].musk-editor-hover {
          background-color: var(--musk-chat-editor-text-hover-bg, rgba(0, 71, 55, 0.20)) !important;
          border: 1px dashed var(--musk-chat-editor-text-hover-border, rgba(0, 71, 55, 0.70)) !important;
        }

        [${CONFIG.EDITABLE_ATTR}="true"].musk-editor-editing {
          border: 2px solid var(--musk-v3-component-editor-selection-border) !important;
          cursor: text !important;
          outline: none !important;
        }

        [contenteditable="true"] {
          outline: none !important;
          caret-color: var(--musk-semantic-chat-input-caret, #00936A);
        }
        [contenteditable="true"]:focus { outline: none !important; }

        input.musk-ai-input {
          caret-color: var(--musk-semantic-chat-input-caret, #00936A);
        }

        a {
          pointer-events: none !important;
          cursor: default !important;
          opacity: 0.7;
        }

        video, audio, iframe, object, embed {
          pointer-events: none !important;
        }

        /* ===== 通用元素 hover ===== */
        .musk-general-hover {
          background-color: var(--musk-chat-editor-element-hover-bg, rgba(0, 71, 55, 0.08)) !important;
          border: 1px dashed var(--musk-chat-editor-element-hover-border, rgba(0, 71, 55, 0.50)) !important;
          cursor: pointer !important;
        }
        .musk-general-editing {
          border: 2px solid var(--musk-v3-component-editor-selection-border) !important;
          background-color: var(--musk-chat-editor-element-active-bg, rgba(0, 71, 55, 0.04)) !important;
        }

        /* ===== AI 输入行（通用） ===== */
        .musk-ai-row {
          display: flex;
          align-items: center;
          padding: 4px 0;
          width: 100%;
        }
        .musk-ai-row > * + * { margin-left: 6px; }
        .musk-ai-row .musk-ai-label {
          font-size: 11px;
          color: var(--musk-chat-editor-primary-weak-bg, #02543B);
          font-weight: 600;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .musk-ai-row .musk-ai-input {
          flex: 1;
          min-width: 0;
          height: 28px;
          border: 1px solid #e5e6eb;
          border-radius: 6px;
          padding: 0 8px;
          font-size: 12px;
          outline: none;
          background: #fff;
          color: var(--musk-v3-component-editor-toolbar-control-text);
          transition: border-color 0.2s;
        }
        .musk-ai-row .musk-ai-input:focus {
          border-color: var(--musk-v3-component-editor-toolbar-primary-bg);
        }
        .musk-ai-row .musk-ai-input::placeholder {
          color: var(--musk-v3-component-editor-toolbar-placeholder);
        }
        /* 通用发送按钮（4 处面板复用：文字/图片/公式/AI 浮窗） */
        .musk-ai-send {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          min-width: 36px;
          border: none;
          border-radius: 12px;
          background: var(--musk-v3-component-editor-toolbar-primary-bg);
          color: var(--musk-v3-component-editor-toolbar-primary-text);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .musk-ai-send:hover { background: var(--musk-v3-component-editor-toolbar-primary-hover-bg); }
        .musk-ai-send:disabled { background: var(--musk-v3-component-editor-toolbar-primary-disabled-bg); cursor: not-allowed; }
        .musk-ai-send.musk-ai-loading {
          background: var(--musk-v3-component-editor-toolbar-primary-bg);
          cursor: wait;
          pointer-events: none;
          position: relative;
        }
        .musk-ai-send.musk-ai-loading svg { visibility: hidden; }
        /* loading spinner：单元素 + 8 段 box-shadow + 整体旋转，模拟 lottie 8-blade 旋转效果 */
        .musk-ai-send.musk-ai-loading::after {
          content: '';
          position: absolute;
          width: 3px; height: 3px;
          border-radius: 50%;
          background: transparent;
          animation: muskAiSpin 0.8s linear infinite;
          box-shadow:
            0 -8px 0 rgba(255,255,255,1),
            5.66px -5.66px 0 rgba(255,255,255,0.875),
            8px 0 0 rgba(255,255,255,0.75),
            5.66px 5.66px 0 rgba(255,255,255,0.625),
            0 8px 0 rgba(255,255,255,0.5),
            -5.66px 5.66px 0 rgba(255,255,255,0.375),
            -8px 0 0 rgba(255,255,255,0.25),
            -5.66px -5.66px 0 rgba(255,255,255,0.125);
        }
        @keyframes muskAiSpin { to { transform: rotate(360deg); } }

        /* ===== AI 通用面板 ===== */
        .musk-ai-panel {
          position: fixed;
          z-index: 2147483646;
          display: none;
          width: 380px;
          max-width: calc(100vw - 20px);
          border-radius: 12px;
          background: var(--musk-v3-component-editor-ai-toolbar-bg, #F5F6F7);
          box-shadow: var(--musk-v3-component-editor-ai-toolbar-shadow, 0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04));
          border: 0.5px solid var(--musk-v3-component-editor-ai-toolbar-border, transparent);
          padding: 4px 4px 4px 14px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        .musk-ai-panel .musk-ai-row {
          padding: 0;
        }
        .musk-ai-panel .musk-ai-label {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 0;
        }
        .musk-ai-panel .musk-ai-input {
          border: none !important;
          background: transparent !important;
          height: 40px;
          font-size: 14px;
          color: var(--musk-v3-component-editor-result-card-text);
        }
        .musk-ai-panel .musk-ai-input:focus {
          border: none !important;
          box-shadow: none !important;
        }
        .musk-ai-panel .musk-ai-input::placeholder {
          color: var(--musk-semantic-chat-input-placeholder, #A9B2AF);
        }
        .musk-ai-panel .musk-ai-preview {
          background: #fff;
        }
        .musk-ai-preview {
          display: none;
          margin-top: 8px;
          padding: 8px 10px;
          background: var(--musk-v3-component-editor-result-card-bg);
          border: 0.5px solid var(--musk-v3-component-editor-result-card-border);
          border-radius: 6px;
          font-size: 12px;
          color: var(--musk-v3-component-editor-result-card-text);
          line-height: 1.6;
          max-height: 160px;
          overflow-y: auto;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .musk-ai-preview.musk-ai-preview-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          color: #86909c;
        }
        .musk-ai-preview-loading .musk-ai-dots span {
          display: inline-block;
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--musk-chat-editor-primary-weak-bg, #02543B);
          margin: 0 3px;
          animation: muskDotBounce 1.2s ease-in-out infinite;
        }
        .musk-ai-preview-loading .musk-ai-dots span:nth-child(2) { animation-delay: 0.2s; }
        .musk-ai-preview-loading .musk-ai-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes muskDotBounce { 0%,80%,100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.2); } }
        .musk-ai-preview-actions {
          display: none;
          margin-top: 6px;
          justify-content: flex-end;
        }
        .musk-ai-preview-actions > * + * { margin-left: 8px; }
        .musk-ai-preview-actions button {
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          border: 1px solid var(--musk-v3-component-editor-result-card-action-border);
          background: var(--musk-v3-component-editor-result-card-action-bg);
          color: var(--musk-v3-component-editor-result-card-action-text);
          transition: all 0.2s;
        }
        .musk-ai-preview-actions button:hover {
          border-color: var(--musk-v3-component-editor-result-card-action-hover-bg);
          background: var(--musk-v3-component-editor-result-card-action-hover-bg);
          color: var(--musk-v3-component-editor-result-card-action-text);
        }
        .musk-ai-preview-actions .musk-ai-regenerate {
          display: flex;
          align-items: center;
        }
        .musk-ai-preview-actions .musk-ai-regenerate > * + * { margin-left: 4px; }
        .musk-ai-preview-actions .musk-ai-apply {
          background: var(--musk-v3-component-editor-result-card-action-primary-bg);
          color: var(--musk-v3-component-editor-result-card-action-primary-text);
          border-color: var(--musk-v3-component-editor-result-card-action-primary-bg);
        }
        .musk-ai-preview-actions .musk-ai-apply:hover {
          background: var(--musk-v3-component-editor-result-card-action-primary-hover-bg);
          border-color: var(--musk-v3-component-editor-result-card-action-primary-hover-bg);
        }

        /* ===== 图片编辑面板 ===== */
        .musk-image-panel {
          position: fixed; z-index: 2147483646; display: none;
          flex-direction: column;
          background: transparent;
          font-family: "PingFang SC", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-size: 14px; user-select: none; white-space: nowrap;
          max-width: calc(100vw - 20px);
        }
        .musk-image-panel .mip-card {
          background: var(--musk-v3-component-editor-toolbar-bg);
          box-shadow: var(--musk-v3-component-editor-toolbar-shadow, 0px 2px 16px 0px rgba(77, 81, 77, 0.06));
          border: 0.5px solid var(--musk-v3-component-editor-toolbar-border);
          border-radius: 12px;
        }
        .musk-image-panel .mip-toolbar-card {
          padding: 4px 4px 4px 8px;
        }
        .musk-image-panel .mip-result-card {
          margin-top: 8px;
          padding: 10px 16px 4px;
          white-space: normal;
          background: var(--musk-v3-component-editor-result-card-bg);
          border-color: var(--musk-v3-component-editor-result-card-border);
        }
        .musk-image-panel .mip-buttons-row {
          display: flex; align-items: center;
        }
        .musk-image-panel .mip-divider {
          width: 1px; height: 20px; background: var(--musk-v3-component-editor-toolbar-border);
          margin: 0 4px; flex-shrink: 0;
        }
        .mip-btn {
          display: flex; align-items: center; justify-content: center;
          height: 32px; min-width: 32px; padding: 0 6px;
          border: none; border-radius: 8px;
          background: var(--musk-v3-component-editor-toolbar-control-bg); color: var(--musk-v3-component-editor-toolbar-control-text); cursor: pointer;
          transition: background 0.15s; font-size: 14px; line-height: 1;
          position: relative;
        }
        .mip-btn > * + * { margin-left: 4px; }
        .mip-btn:hover { background: var(--musk-v3-component-editor-toolbar-control-hover-bg); }
        .mip-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .mip-btn svg { display: block; flex-shrink: 0; }

        /* Tooltip on icon buttons (mirrors mst-btn tooltip) */
        .mip-btn[data-tooltip]::before {
          content: attr(data-tooltip);
          position: absolute; bottom: calc(100% + 8px); left: 50%;
          transform: translateX(-50%);
          background: var(--musk-v3-component-editor-toolbar-primary-bg); color: var(--musk-v3-component-editor-toolbar-primary-text);
          font-size: 13px; line-height: 1.2;
          padding: 6px 10px; border-radius: 8px;
          white-space: nowrap; pointer-events: none;
          opacity: 0; transition: opacity 0.15s; z-index: 11;
        }
        .mip-btn[data-tooltip]::after {
          content: ''; position: absolute;
          bottom: calc(100% + 4px); left: 50%; transform: translateX(-50%) rotate(45deg);
          width: 9px; height: 9px;
          background: var(--musk-v3-component-editor-toolbar-primary-bg);
          pointer-events: none; opacity: 0; transition: opacity 0.15s;
          z-index: 10;
        }
        .mip-btn[data-tooltip]:hover::before,
        .mip-btn[data-tooltip]:hover::after { opacity: 1; }

        /* object-fit dropdown */
        .mip-fit-wrap { position: relative; }
        .mip-fit-btn .mip-fit-label {
          font-size: 14px; color: var(--musk-v3-component-editor-toolbar-panel-text); font-weight: 400;
          padding: 0 2px;
        }
        .mip-fit-dropdown {
          color: var(--musk-v3-component-editor-toolbar-panel-text);
          display: none; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
          margin-top: 10px; padding: 4px; min-width: 108px;
          background: var(--musk-v3-component-editor-toolbar-bg); border-radius: 8px;
          border: 0.5px solid var(--musk-v3-component-editor-toolbar-border);
          box-shadow: 0px 4px 16px 0px rgba(76, 97, 84, 0.12);
          z-index: 5;
        }
        .mip-fit-wrap.mip-open .mip-fit-dropdown { display: block; }
        .mip-fit-dropdown.mip-drop-up {
          top: auto; bottom: 100%;
          margin-top: 0; margin-bottom: 10px;
        }
        .mip-fit-option {
          padding: 8px 12px 8px 36px; border-radius: 8px; cursor: pointer;
          font-size: 14px; color: var(--musk-v3-component-editor-toolbar-control-text); position: relative;
        }
        .mip-fit-option:hover { background: var(--musk-v3-component-editor-toolbar-control-hover-bg); }
        .mip-fit-option.mip-selected {
          background: var(--musk-v3-component-editor-toolbar-control-active-bg); font-weight: 400;
        }
        .mip-fit-option .mst-option-check {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          display: none; line-height: 0; color: var(--musk-v3-component-editor-toolbar-panel-text);
        }
        .mip-fit-option.mip-selected .mst-option-check { display: block; }
        .mip-fit-option .mst-option-label { display: block; }

        /* Upload button (icon + label) */
        .mip-upload-btn { padding: 0 8px; }
        .mip-upload-label {
          font-size: 14px; color: var(--musk-v3-component-editor-toolbar-panel-text); font-weight: 400;
          padding: 0 2px;
        }
        /* 左侧图标槽位：静态 icon 与 loading spinner 互斥占位，避免切换时影响兄弟元素 margin */
        .mip-upload-btn .mip-upload-icon-slot {
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .mip-upload-btn .mip-upload-spinner { display: none; }
        .mip-upload-btn.mip-uploading,
        .mip-upload-btn.mip-upload-disabled { pointer-events: none; }
        /* 置灰不可点击态：SVG 结构不变，仅通过 currentColor 使用编辑器专属置灰色。 */
        .mip-upload-btn.mip-upload-disabled {
          color: var(--musk-v3-component-editor-toolbar-replace-disabled-text);
          opacity: var(--musk-v3-component-editor-toolbar-replace-disabled-opacity);
          cursor: not-allowed;
        }
        .mip-upload-btn.mip-upload-disabled .mip-upload-label {
          color: var(--musk-v3-component-editor-toolbar-replace-disabled-text);
        }
        .mip-upload-btn.mip-upload-disabled:hover { background: transparent; }
        /* 加载中：隐藏静态 icon、显示旋转 spinner，文字"替换"保持可见 */
        .mip-upload-btn.mip-uploading .mip-upload-icon { display: none; }
        .mip-upload-btn.mip-uploading .mip-upload-spinner { display: inline-flex; }
        .mip-upload-btn.mip-uploading .mip-upload-spinner svg {
          animation: muskAiSpin 1s linear infinite;
        }

        /* AI section (inline within toolbar) */
        .musk-image-panel .mip-ai-section {
          display: flex; align-items: center;
          flex: 1; min-width: 280px;
          padding-left: 8px;
        }
        .musk-image-panel .mip-ai-section .musk-ai-label {
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; font-size: 0; margin-right: 6px;
        }
        .musk-image-panel .mip-ai-section .musk-ai-input {
          flex: 1; min-width: 80px; height: 40px;
          border: none !important; background: transparent !important;
          font-size: 14px; color: var(--musk-v3-component-editor-toolbar-panel-text); outline: none;
          padding: 0 8px;
        }
        .musk-image-panel .mip-ai-section .musk-ai-input:focus { box-shadow: none !important; }
        .musk-image-panel .mip-ai-section .musk-ai-input::placeholder { color: var(--musk-v3-component-editor-toolbar-placeholder); }
        /* Result card */
        .musk-image-panel .mip-result-card .mip-image-preview {
          width: 100%; line-height: 0;
          display: flex; justify-content: center;
        }
        /* 用 max-width / max-height 双上限保持原比例，避免高比例图把面板撑得过高 */
        .musk-image-panel .mip-result-card .mip-image-preview img {
          display: block;
          max-width: 100%;
          max-height: 400px;
          border-radius: 8px;
        }
        .musk-image-panel .mip-result-card .mip-image-actions {
          display: flex; justify-content: flex-start;
          margin: 10px -2px 0; padding: 4px 0 0;
          border-top: 1px solid var(--musk-v3-component-editor-result-card-divider);
        }
        .musk-image-panel .mip-result-card .mip-image-actions > * + * { margin-left: 4px; }
        .mip-action-btn {
          display: flex; align-items: center;
          padding: 6px 10px; border: none; background: transparent;
          color: var(--musk-v3-component-editor-result-card-action-text); font-size: 13px; border-radius: 6px;
          cursor: pointer; transition: background 0.15s;
        }
        .mip-action-btn:hover { background: var(--musk-v3-component-editor-result-card-action-hover-bg); }
        .mip-action-btn svg { margin-right: 4px; flex-shrink: 0; }

        /* ===== 公式元素 ===== */
        mjx-container {
          cursor: pointer !important;
          transition: border 0.15s, background-color 0.15s;
          border-radius: 4px;
        }
        mjx-container.musk-formula-hover {
          border: 1px dashed var(--musk-chat-editor-element-hover-border, rgba(0, 71, 55, 0.50)) !important;
          background-color: var(--musk-chat-editor-element-hover-bg, rgba(0, 71, 55, 0.08)) !important;
        }
        mjx-container.musk-formula-editing {
          border: 2px solid var(--musk-v3-component-editor-selection-border) !important;
          background-color: var(--musk-chat-editor-element-active-bg, rgba(0, 71, 55, 0.04)) !important;
        }

        /* ===== 公式编辑面板 (MathLive) ===== */
        .musk-formula-panel {
          position: fixed; z-index: 2147483646; display: none;
          flex-direction: column;
          background: transparent;
          font-family: "PingFang SC", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-size: 14px; user-select: none; white-space: nowrap;
          max-width: calc(100vw - 20px);
        }
        .musk-formula-panel .mfp-card {
          background: var(--musk-v3-component-editor-toolbar-bg);
          box-shadow: var(--musk-v3-component-editor-toolbar-shadow, 0px 2px 16px 0px rgba(77, 81, 77, 0.06));
          border: 0.5px solid var(--musk-v3-component-editor-toolbar-border);
          border-radius: 12px;
        }
        .musk-formula-panel .mfp-toolbar-card {
          padding: 4px 4px 4px 8px;
        }
        .musk-formula-panel .mfp-result-card {
          margin-top: 8px;
          padding: 10px 16px 4px;
          white-space: normal;
          width: 394px; max-width: calc(100vw - 20px);
          background: var(--musk-v3-component-editor-result-card-bg);
          border-color: var(--musk-v3-component-editor-result-card-border);
        }
        .musk-formula-panel .mfp-manual-card {
          padding: 4px;
        }
        .musk-formula-panel .mfp-buttons-row,
        .musk-formula-panel .mfp-manual-row {
          display: flex; align-items: center;
        }
        .musk-formula-panel .mfp-divider {
          width: 1px; height: 20px; background: var(--musk-v3-component-editor-toolbar-border);
          margin: 0 4px; flex-shrink: 0;
        }
        .mfp-btn {
          display: flex; align-items: center; justify-content: center;
          height: 32px; min-width: 32px; padding: 0 6px;
          border: none; border-radius: 8px;
          background: var(--musk-v3-component-editor-toolbar-control-bg); color: var(--musk-v3-component-editor-toolbar-control-text); cursor: pointer;
          transition: background 0.15s; font-size: 14px; line-height: 1;
          position: relative;
        }
        .mfp-btn > * + * { margin-left: 4px; }
        .mfp-btn:hover { background: var(--musk-v3-component-editor-toolbar-control-hover-bg); }
        .mfp-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .mfp-btn svg { display: block; flex-shrink: 0; }

        /* Tooltip on icon buttons (mirrors mst/mip-btn tooltip) */
        .mfp-btn[data-tooltip]::before {
          content: attr(data-tooltip);
          position: absolute; bottom: calc(100% + 8px); left: 50%;
          transform: translateX(-50%);
          background: var(--musk-v3-component-editor-toolbar-primary-bg); color: var(--musk-v3-component-editor-toolbar-primary-text);
          font-size: 13px; line-height: 1.2;
          padding: 6px 10px; border-radius: 8px;
          white-space: nowrap; pointer-events: none;
          opacity: 0; transition: opacity 0.15s; z-index: 11;
        }
        .mfp-btn[data-tooltip]::after {
          content: ''; position: absolute;
          bottom: calc(100% + 4px); left: 50%; transform: translateX(-50%) rotate(45deg);
          width: 9px; height: 9px;
          background: var(--musk-v3-component-editor-toolbar-primary-bg);
          pointer-events: none; opacity: 0; transition: opacity 0.15s;
          z-index: 10;
        }
        .mfp-btn[data-tooltip]:hover::before,
        .mfp-btn[data-tooltip]:hover::after { opacity: 1; }

        /* "编辑公式" 触发按钮 */
        .mfp-edit-trigger { padding: 0 8px; }
        .mfp-edit-label {
          font-size: 14px; color: var(--musk-v3-component-editor-toolbar-panel-text); font-weight: 400;
          padding: 0 2px;
        }

        /* AI section (inline within toolbar card) */
        .musk-formula-panel .mfp-ai-section {
          display: flex; align-items: center;
          flex: 1; min-width: 280px;
        }
        .musk-formula-panel .mfp-ai-section .musk-ai-label {
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; font-size: 0; margin-right: 6px;
        }
        .musk-formula-panel .mfp-ai-section .musk-ai-input {
          flex: 1; min-width: 80px; height: 40px;
          border: none !important; background: transparent !important;
          font-size: 14px; color: var(--musk-v3-component-editor-toolbar-panel-text); outline: none;
          padding: 0 8px;
        }
        .musk-formula-panel .mfp-ai-section .musk-ai-input:focus { box-shadow: none !important; }
        .musk-formula-panel .mfp-ai-section .musk-ai-input::placeholder { color: var(--musk-v3-component-editor-toolbar-placeholder); }
        /* Manual edit row: math-field + 虚拟键盘按钮 + 菜单按钮 */
        .musk-formula-panel .mfp-manual-row {
          width: 100%;
        }
        .musk-formula-panel .mfp-manual-row > * + * { margin-left: 4px; }
        .mfp-mathfield-wrap {
          flex: 1;
          min-width: 280px; max-width: 520px;
          background: #fff; border-radius: 8px;
          padding: 6px 10px; min-height: 40px;
          display: flex; align-items: center;
        }
        .mfp-mathfield-wrap math-field {
          display: block; width: 100%; font-size: 18px;
          border: none; outline: none; background: transparent;
          --caret-color: var(--musk-semantic-chat-input-caret, #00936A);
        }
        .mfp-loading {
          display: flex; align-items: center;
          height: 28px; color: #999; font-size: 12px;
        }
        /* Manual edit 右侧图标按钮：虚拟键盘 / 菜单 */
        .musk-formula-panel .mfp-icon-btn {
          flex-shrink: 0;
          width: 36px; height: 36px;
          display: flex; align-items: center; justify-content: center;
          border: none; border-radius: 8px;
          background: var(--musk-v3-component-editor-toolbar-control-bg); color: var(--musk-v3-component-editor-toolbar-control-text);
          cursor: pointer; transition: background 0.15s;
          position: relative; padding: 0;
        }
        .musk-formula-panel .mfp-icon-btn:hover { background: var(--musk-v3-component-editor-toolbar-control-hover-bg); }
        .musk-formula-panel .mfp-icon-btn.mfp-icon-btn-active { background: var(--musk-v3-component-editor-toolbar-control-active-bg); }
        .musk-formula-panel .mfp-icon-btn svg { display: block; }
        /* Tooltip on icon buttons (mirrors mfp-btn tooltip) */
        .musk-formula-panel .mfp-icon-btn[data-tooltip]::before {
          content: attr(data-tooltip);
          position: absolute; bottom: calc(100% + 8px); left: 50%;
          transform: translateX(-50%);
          background: var(--musk-v3-component-editor-toolbar-primary-bg); color: var(--musk-v3-component-editor-toolbar-primary-text);
          font-size: 13px; line-height: 1.2;
          padding: 6px 10px; border-radius: 8px;
          white-space: nowrap; pointer-events: none;
          opacity: 0; transition: opacity 0.15s; z-index: 11;
        }
        .musk-formula-panel .mfp-icon-btn[data-tooltip]::after {
          content: ''; position: absolute;
          bottom: calc(100% + 4px); left: 50%; transform: translateX(-50%) rotate(45deg);
          width: 9px; height: 9px;
          background: var(--musk-v3-component-editor-toolbar-primary-bg);
          pointer-events: none; opacity: 0; transition: opacity 0.15s;
          z-index: 10;
        }
       .musk-formula-panel .mfp-icon-btn[data-tooltip]:hover::before,
       .musk-formula-panel .mfp-icon-btn[data-tooltip]:hover::after { opacity: 1; }
        /* tooltip 需要越过面板边界显示；非悬停状态仍保留面板滚动 */
        .musk-formula-panel:has(.mfp-icon-btn[data-tooltip]:hover) { overflow: visible !important; }
       /* 手动编辑面板右侧的"确定"按钮：点击后将编辑内容回写 HTML 并关闭面板 */
        .musk-formula-panel .mfp-confirm-btn {
          flex-shrink: 0;
          height: 36px;
          padding: 0 16px;
          border: none;
          border-radius: 12px;
          background: var(--musk-v3-component-editor-toolbar-primary-bg);
          color: var(--musk-v3-component-editor-toolbar-primary-text);
          font-size: 14px;
          font-weight: 400;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .musk-formula-panel .mfp-confirm-btn:hover { background: var(--musk-v3-component-editor-toolbar-primary-hover-bg); }
        .musk-formula-panel .mfp-confirm-btn:disabled { background: var(--musk-v3-component-editor-toolbar-primary-disabled-bg); cursor: not-allowed; }
        /* 隐藏 MathLive 原生 keyboard toggle，避免与自定义按钮重复 */
        .mfp-mathfield-wrap math-field::part(virtual-keyboard-toggle) { display: none; }
        .mfp-mathfield-wrap math-field::part(menu-toggle) { display: none; }

        /* Result card content */
        .musk-formula-panel .mfp-formula-preview {
          min-height: 60px; padding: 8px 4px 12px;
          display: flex; align-items: center; justify-content: center;
          overflow-x: auto; overflow-y: hidden;
        }
        .musk-formula-panel .mfp-formula-preview mjx-container {
          font-size: 1.3em !important;
          cursor: default !important;
          border: none !important;
          background-color: transparent !important;
        }
        .musk-formula-panel .mfp-result-actions {
          display: flex; justify-content: flex-start;
          margin: 0 -2px; padding: 4px 0 0;
          border-top: 1px solid var(--musk-v3-component-editor-result-card-divider);
        }
        .musk-formula-panel .mfp-result-actions > * + * { margin-left: 4px; }
        .mfp-action-btn {
          display: flex; align-items: center;
          padding: 6px 10px; border: none; background: transparent;
          color: var(--musk-v3-component-editor-result-card-action-text); font-size: 13px; border-radius: 6px;
          cursor: pointer; transition: background 0.15s;
        }
        .mfp-action-btn:hover { background: var(--musk-v3-component-editor-result-card-action-hover-bg); }
        .mfp-action-btn svg { margin-right: 4px; flex-shrink: 0; }

        math-virtual-keyboard {
          z-index: 2147483647 !important;
          pointer-events: auto !important;
        }

        /* ===== 编辑器工具栏与结果卡片 ===== */
        /* 各面板共享状态 Token，布局、内容和 SVG 仍由各自面板维护。 */
        .musk-style-toolbar {
          position: fixed; z-index: 2147483647; display: none;
          flex-direction: column;
          background: transparent;
          font-family: "PingFang SC", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-size: 14px; user-select: none; white-space: nowrap;
        }
        .musk-style-toolbar .mst-card {
          background: var(--musk-v3-component-editor-toolbar-bg);
          box-shadow: var(--musk-v3-component-editor-toolbar-shadow, 0px 2px 16px 0px rgba(77, 81, 77, 0.06));
          border: 0.5px solid var(--musk-v3-component-editor-toolbar-border);
          border-radius: 12px;
        }
        .musk-style-toolbar .mst-toolbar-card {
          padding: 4px 4px 4px 8px;
        }
        .musk-style-toolbar .mst-result-card {
          margin-top: 8px;
          padding: 14px 16px 6px;
          white-space: normal;
          background: var(--musk-v3-component-editor-result-card-bg);
          box-shadow: 0px 2px 16px 0px rgba(77, 81, 77, 0.06);
          border: 0.5px solid var(--musk-v3-component-editor-result-card-border);
          border-radius: 12px;
        }
        .mst-buttons-row { display: flex; align-items: center; }
        .mst-buttons-row > * + * { margin-left: 2px; }
        .mst-group { display: flex; align-items: center; }
        .mst-group > * + * { margin-left: 2px; }
        .mst-divider { width: 1px; height: 20px; background: var(--musk-v3-component-editor-toolbar-border); margin: 0 4px; flex-shrink: 0; }
        .mst-btn {
          display: flex; align-items: center; justify-content: center;
          height: 32px; width: 32px;
          border: none; border-radius: 8px;
          background: var(--musk-v3-component-editor-toolbar-control-bg); color: var(--musk-v3-component-editor-toolbar-control-text); cursor: pointer;
          transition: background 0.15s;
        }
        .mst-btn:hover { background: var(--musk-v3-component-editor-toolbar-control-hover-bg); }
        .mst-btn.mst-active { background: var(--musk-v3-component-editor-toolbar-control-active-bg); }

        /* Tooltip */
        .mst-btn[data-tooltip] { position: relative; }
        .mst-btn[data-tooltip]::before {
          content: attr(data-tooltip);
          position: absolute;
          bottom: calc(100% + 8px);
          left: 50%; transform: translateX(-50%);
          background: var(--musk-v3-component-editor-toolbar-primary-bg); color: var(--musk-v3-component-editor-toolbar-primary-text);
          font-size: 13px; line-height: 1.2;
          padding: 6px 10px; border-radius: 8px;
          white-space: nowrap; pointer-events: none;
          opacity: 0; transition: opacity 0.15s;
          z-index: 11;
        }
        .mst-btn[data-tooltip]::after {
          content: '';
          position: absolute;
          bottom: calc(100% + 4px);
          left: 50%; transform: translateX(-50%) rotate(45deg);
          width: 9px; height: 9px;
          background: var(--musk-v3-component-editor-toolbar-primary-bg);
          pointer-events: none;
          opacity: 0; transition: opacity 0.15s;
          z-index: 10;
        }
        .mst-btn[data-tooltip]:hover::before,
        .mst-btn[data-tooltip]:hover::after { opacity: 1; }

        /* AI 发送按钮属于工具条本身；使用专用 Token 覆盖通用 AI 面板的旧主题值。 */
        .musk-style-toolbar .musk-ai-send {
          background: var(--musk-v3-component-editor-toolbar-primary-bg);
          color: var(--musk-v3-component-editor-toolbar-primary-text);
        }
        .musk-style-toolbar .musk-ai-send:hover {
          background: var(--musk-v3-component-editor-toolbar-primary-hover-bg);
        }
        .musk-style-toolbar .musk-ai-send:disabled {
          background: var(--musk-v3-component-editor-toolbar-primary-disabled-bg);
        }

        /* Font size */
        .mst-fontsize-wrap { position: relative; }
        .mst-btn > * + * { margin-left: 3px; }
        .mst-fontsize-btn { width: 84px; padding: 0 6px; }
        .mst-fontsize-btn > * + * { margin-left: 3px; }
        .mst-fontsize-value { font-size: 16px; min-width: 20px; text-align: center; font-variant-numeric: tabular-nums; font-weight: 400; }
        .mst-arrow { opacity: 0.5; flex-shrink: 0; }
        .mst-fontsize-dropdown {
          color: var(--musk-v3-component-editor-toolbar-panel-text);
          display: none; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
          margin-top: 10px; padding: 4px;
          width: 98px;
          box-sizing: border-box;
          background: var(--musk-v3-component-editor-toolbar-bg); border-radius: 8px;
          border: 0.5px solid var(--musk-v3-component-editor-toolbar-border);
          box-shadow: 0px 4px 16px 0px rgba(76, 97, 84, 0.12);
          max-height: 320px; overflow-y: auto;
        }
        .mst-fontsize-dropdown.mst-open { display: block; }
        /* 朝上展开（当 toolbar 在 viewport 下半部分、向下空间不足时由 JS 切换） */
        .mst-fontsize-dropdown.mst-drop-up {
          top: auto; bottom: 100%;
          margin-top: 0; margin-bottom: 10px;
        }
        .mst-fontsize-option {
          padding: 8px 12px; border-radius: 8px; cursor: pointer;
          text-align: center; font-size: 16px; color: var(--musk-v3-component-editor-toolbar-control-text);
          position: relative;
        }
        .mst-fontsize-option:hover { background: var(--musk-v3-component-editor-toolbar-control-hover-bg); }
        .mst-fontsize-option.mst-selected {
          background: var(--musk-v3-component-editor-toolbar-control-active-bg); font-weight: 400;
        }
        .mst-fontsize-option .mst-option-check {
          position: absolute; left: 8px; top: 50%; transform: translateY(-50%);
          display: none; line-height: 0; color: var(--musk-v3-component-editor-toolbar-panel-text);
        }
        .mst-fontsize-option.mst-selected .mst-option-check { display: block; }

        /* Color swatch */
        .mst-color-wrap { position: relative; }
        .mst-color-btn { width: 50px; padding: 0 4px; }
        .mst-color-swatch {
          display: block; width: 16px; height: 16px; border-radius: 4px;
          border: 1px solid rgba(0,0,0,0.1); flex-shrink: 0;
        }
        .mst-color-dropdown {
          display: none; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
          margin-top: 10px; padding: 4px;
          background: var(--musk-v3-component-editor-toolbar-bg); border-radius: 8px;
          border: 0.5px solid var(--musk-v3-component-editor-toolbar-border);
          box-shadow: 0px 4px 16px 0px rgba(76, 97, 84, 0.12);
        }
        .mst-color-dropdown.mst-open { display: block; }
        .mst-color-dropdown.mst-drop-up {
          top: auto; bottom: 100%;
          margin-top: 0; margin-bottom: 10px;
        }
        .mst-color-grid {
          display: flex; flex-wrap: wrap;
          width: 180px; margin: -2px;
        }
        .mst-color-item {
          width: 26px; height: 26px; border-radius: 5px; cursor: pointer;
          border: 1px solid rgba(0, 0, 0, 0.04);
          box-sizing: border-box; margin: 2px;
        }
        .mst-color-none {
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; color: #999; background: #f5f5f5;
        }

        /* Alignment dropdown */
        .mst-align-wrap { position: relative; }
        .mst-align-trigger { width: 50px; padding: 0 4px; }
        .mst-align-trigger > * + * { margin-left: 3px; }
        .mst-align-dropdown {
width: 98px;
box-sizing: border-box;
          color: var(--musk-v3-component-editor-toolbar-panel-text);
          display: none; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
          margin-top: 10px; padding: 4px;
          background: var(--musk-v3-component-editor-toolbar-bg); border-radius: 8px;
          border: 0.5px solid var(--musk-v3-component-editor-toolbar-border);
          box-shadow: 0px 4px 16px 0px rgba(76, 97, 84, 0.12);
        }
        .mst-align-dropdown.mst-open { display: block; }
        .mst-align-dropdown.mst-drop-up {
          top: auto; bottom: 100%;
          margin-top: 0; margin-bottom: 10px;
        }
        .mst-align-option {
          display: flex; align-items: center; justify-content: center;
          height: 32px; border-radius: 8px; cursor: pointer;
          color: var(--musk-v3-component-editor-toolbar-control-text); transition: background 0.15s;
          position: relative;
        }
        .mst-align-option:hover { background: var(--musk-v3-component-editor-toolbar-control-hover-bg); }
        .mst-align-option.mst-selected { background: var(--musk-v3-component-editor-toolbar-control-active-bg); }
        .mst-align-option .mst-option-check {
          position: absolute; left: 8px; top: 50%; transform: translateY(-50%);
          display: none; line-height: 0; color: var(--musk-v3-component-editor-toolbar-panel-text);
        }
        .mst-align-option.mst-selected .mst-option-check { display: block; }

        /* AI section in toolbar */
        .mst-ai-section {
          display: flex; align-items: center; flex: 1; min-width: 0;
        }
        .mst-ai-section .musk-ai-label {
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; font-size: 0; margin-right: 6px;
        }
        .mst-ai-section .musk-ai-input {
          flex: 1; min-width: 80px; height: 40px;
          border: none !important; background: transparent !important;
          font-size: 14px; color: var(--musk-v3-component-editor-toolbar-panel-text); outline: none;
          padding: 0 8px;
        }
        .mst-ai-section .musk-ai-input:focus { box-shadow: none !important; }
        .mst-ai-section .musk-ai-input::placeholder { color: var(--musk-v3-component-editor-toolbar-placeholder); }
        /* Toolbar AI result card */
        .musk-style-toolbar .mst-result-card .musk-ai-preview {
          display: block; background: transparent; margin: 0; padding: 0;
          font-size: 16px; color: var(--musk-v3-component-editor-result-card-text); line-height: 26px;
          max-height: 78px; overflow-y: auto;
          word-break: break-word;
        }
        .musk-style-toolbar .mst-result-card .musk-ai-preview-actions {
          display: flex; justify-content: flex-start;
          margin: 12px -8px 0; padding: 4px 0 0;
          border-top: 1px solid var(--musk-v3-component-editor-result-card-divider);
        }
        .musk-style-toolbar .mst-result-card .musk-ai-preview-actions > * + * { margin-left: 10px; }
        .musk-style-toolbar .mst-result-card .musk-ai-preview-actions button {
          width:58px;
          display: flex; align-items: center;
          padding: 4px; border: none; background: transparent;
          color: var(--musk-v3-component-editor-result-card-action-text); font-size: 14px;
          cursor: pointer; transition: background 0.15s;
        }
        .musk-style-toolbar .mst-result-card .musk-ai-preview-actions button:hover { background: var(--musk-v3-component-editor-result-card-action-hover-bg); }
        .musk-style-toolbar .mst-result-card .musk-ai-preview-actions button svg { margin-right: 4px; flex-shrink: 0; }

        .mst-fontsize-dropdown::-webkit-scrollbar { width: 4px; }
        .mst-fontsize-dropdown::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 2px; }
      `

    document.head.appendChild(style)
    this.styleElement = style
    log('编辑器样式已注入')
  }
}
